import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Autocomplete,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  FormControl,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import BlockIcon from "@mui/icons-material/Block";
import EditIcon from "@mui/icons-material/Edit";
import SearchIcon from "@mui/icons-material/Search";
import { ToastContainer, toast } from "react-toastify";
import DeleteConfirmationDialog from "../DeleteConfirmationDialog";
import { BUTTON_STYLES } from "../../styles/componentStyles";
import {
  DISCOUNT_TYPES,
  apiErrorMessage,
  createCoupon,
  deactivateCoupon,
  listCoupons,
  updateCoupon,
} from "../../api/assessmentCoupons";
import { listCategories } from "../../api/assessmentCategories";
import { listTestSeries } from "../../api/assessmentTestSeries";
import useDebouncedValue from "./useDebouncedValue";
import AssessmentTableSkeleton from "./AssessmentTableSkeleton";
import useColumnVisibility from "./useColumnVisibility";
import ColumnsMenuButton from "./ColumnsMenuButton";
import AssessmentEmptyState from "./shell/AssessmentEmptyState";
import AssessmentCouponForm from "./AssessmentCouponForm";
import { formatDiscount, formatRupees, formatValidFrom, formatValidTo } from "./couponFormat";
import {
  TARGET_TYPES,
  buildFilterOptions,
  buildListParams,
  targetTypeLabel,
} from "./couponTargets";

const ACCENT = "#17479e";
const PAGE_SIZE_OPTIONS = [10, 25, 50, 100];

const columns = [
  { key: "code", label: "Coupon Code", sx: { minWidth: 130 } },
  { key: "description", label: "Description", sx: { minWidth: 200, maxWidth: 280 } },
  { key: "appliesTo", label: "Applies to", sx: { minWidth: 220 } },
  { key: "discountType", label: "Discount Type", align: "center", sx: { minWidth: 130 } },
  { key: "discount", label: "Discount Value", align: "center", sx: { minWidth: 110 } },
  { key: "maxDiscount", label: "Max Discount", align: "center", sx: { minWidth: 110 } },
  { key: "perUser", label: "Per-User Limit", align: "center", sx: { minWidth: 110 } },
  { key: "validFrom", label: "Valid From", sx: { minWidth: 160, whiteSpace: "nowrap" } },
  { key: "validTo", label: "Valid Till", sx: { minWidth: 160, whiteSpace: "nowrap" } },
  { key: "usage", label: "Usage", align: "center", sx: { minWidth: 80 } },
  { key: "status", label: "Status", align: "center", sx: { minWidth: 90 } },
];

const statusCfg = {
  ACTIVE: { label: "Active", color: "#2e7d32", bg: "#e8f5e9" },
  INACTIVE: { label: "Inactive", color: "#555", bg: "#f0f0f0" },
};

function StatusChip({ value }) {
  const cfg = statusCfg[value] ?? { label: value ?? "—", color: "#555", bg: "#f5f5f5" };
  return (
    <Chip
      label={cfg.label}
      size="small"
      variant="outlined"
      sx={{ fontWeight: 600, fontSize: "0.7rem", color: cfg.color, borderColor: cfg.color, backgroundColor: cfg.bg }}
    />
  );
}

// Hues match the Access chips on the Test Series screen: blue for a course, purple for a test series.
const targetChipCfg = {
  CATEGORY: { color: "#17479e", bg: "#e8eefb" },
  SERIES: { color: "#6a1b9a", bg: "#f3e5f5" },
};

function TargetChip({ type }) {
  const cfg = targetChipCfg[type] ?? { color: "#555", bg: "#f5f5f5" };
  return (
    <Chip
      label={targetTypeLabel(type)}
      size="small"
      variant="outlined"
      sx={{ fontWeight: 600, fontSize: "0.7rem", color: cfg.color, borderColor: cfg.color, backgroundColor: cfg.bg }}
    />
  );
}

// Every page of a paginated list, for the pickers and the Target filter (a coupon can point at any of them).
async function fetchAllPages(fetchPage) {
  const rows = [];
  for (let page = 0; ; page += 1) {
    const data = await fetchPage(page);
    rows.push(...(data.content || []));
    if (!data.hasNext) break;
  }
  return rows;
}

function AssessmentCoupons() {
  const [coupons, setCoupons] = useState([]);
  const [totalElements, setTotalElements] = useState(0);
  const [loading, setLoading] = useState(true);
  const [courses, setCourses] = useState([]);
  const [series, setSeries] = useState([]);

  const [search, setSearch] = useState("");
  const debouncedSearch = useDebouncedValue(search);
  const { visibleColumns, toggleColumn, visibleColumnDefs } = useColumnVisibility(
    "assessmentCouponsHiddenColumns",
    columns,
  );
  const [typeFilter, setTypeFilter] = useState("");
  const [targetFilter, setTargetFilter] = useState(null);
  const [statusFilter, setStatusFilter] = useState("");
  const [discountTypeFilter, setDiscountTypeFilter] = useState("");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);

  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [deactivateTarget, setDeactivateTarget] = useState(null);

  // Responses can arrive out of order while the admin types or flips filters: only the latest request may
  // update the table.
  const requestSeq = useRef(0);

  const loadCoupons = useCallback(() => {
    const seq = ++requestSeq.current;
    setLoading(true);
    listCoupons(
      buildListParams({
        page,
        size: rowsPerPage,
        search: debouncedSearch,
        targetType: typeFilter,
        targetId: targetFilter?.id ?? "",
        status: statusFilter,
        discountType: discountTypeFilter,
      }),
    )
      .then((data) => {
        if (seq !== requestSeq.current) return;
        // The last page can empty out (for example after a deactivate under an Active filter): step back.
        if (data.content.length === 0 && page > 0 && data.totalPages > 0) {
          setPage(data.totalPages - 1);
          return;
        }
        setCoupons(data.content);
        setTotalElements(data.totalElements ?? 0);
      })
      .catch((err) => {
        if (seq !== requestSeq.current) return;
        setCoupons([]);
        setTotalElements(0);
        toast.error(apiErrorMessage(err, "Failed to load coupons"));
      })
      .finally(() => {
        if (seq === requestSeq.current) setLoading(false);
      });
  }, [page, rowsPerPage, debouncedSearch, typeFilter, targetFilter, statusFilter, discountTypeFilter]);

  useEffect(() => {
    loadCoupons();
  }, [loadCoupons]);

  // The full course and series lists feed the Target filter and the form's picker.
  useEffect(() => {
    fetchAllPages((p) => listCategories({ page: p, size: 100 }))
      .then(setCourses)
      .catch((err) => toast.error(apiErrorMessage(err, "Failed to load courses")));
    fetchAllPages((p) => listTestSeries({ page: p, size: 100 }))
      .then(setSeries)
      .catch((err) => toast.error(apiErrorMessage(err, "Failed to load test series")));
  }, []);

  const targetOptions = useMemo(
    () => buildFilterOptions(typeFilter, courses, series),
    [typeFilter, courses, series],
  );

  // Reset to the first page once the debounced search settles. When the admin is past
  // page 1 this sends one extra request with the old page; requestSeq discards it.
  useEffect(() => {
    setPage(0);
  }, [debouncedSearch]);

  const handleSearchChange = (e) => {
    setSearch(e.target.value);
  };
  const handleTypeChange = (e) => {
    setTypeFilter(e.target.value);
    setTargetFilter(null);
    setPage(0);
  };
  const handleTargetChange = (_, value) => {
    setTargetFilter(value);
    setPage(0);
  };
  const handleStatusChange = (e) => {
    setStatusFilter(e.target.value);
    setPage(0);
  };
  const handleDiscountTypeChange = (e) => {
    setDiscountTypeFilter(e.target.value);
    setPage(0);
  };
  const handleChangePage = (event, newPage) => setPage(newPage);
  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const openAdd = () => {
    setEditing(null);
    setFormOpen(true);
  };

  const handleSave = ({ id, payload }) =>
    (id ? updateCoupon(id, payload) : createCoupon(payload))
      .then(() => {
        toast.success(id ? "Coupon updated successfully!" : "Coupon created successfully!");
        setFormOpen(false);
        // Newest first: a new coupon lands on page 1.
        if (!id && page !== 0) setPage(0);
        else loadCoupons();
      })
      .catch((err) => {
        toast.error(apiErrorMessage(err, "Failed to save coupon"));
      });

  const handleDeactivate = () => {
    const target = deactivateTarget;
    deactivateCoupon(target.id)
      .then(() => {
        toast.success("Coupon deactivated!");
        setDeactivateTarget(null);
        loadCoupons();
      })
      .catch((err) => toast.error(apiErrorMessage(err, "Failed to deactivate coupon")));
  };

  const renderCell = (key, row) => {
    switch (key) {
      case "code":
        return (
          <Typography sx={{ fontWeight: 700, fontSize: "0.85rem", letterSpacing: 0.4 }}>{row.code}</Typography>
        );
      case "description":
        return row.description ? (
          <Tooltip title={row.description}>
            <Typography
              sx={{ fontSize: "0.85rem", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", maxWidth: 260 }}
            >
              {row.description}
            </Typography>
          </Tooltip>
        ) : (
          "—"
        );
      case "appliesTo":
        return (
          <Stack direction="row" spacing={1} alignItems="center">
            <TargetChip type={row.targetType} />
            <Typography sx={{ fontSize: "0.85rem" }}>{row.targetName ?? "—"}</Typography>
          </Stack>
        );
      case "discountType":
        return DISCOUNT_TYPES.find((t) => t.value === row.discountType)?.label ?? row.discountType ?? "—";
      case "discount":
        return formatDiscount(row);
      case "maxDiscount":
        return row.discountType === "PERCENTAGE" && row.maxDiscountAmount != null
          ? formatRupees(row.maxDiscountAmount)
          : "—";
      case "usage":
        return `${row.usageCount} / ${row.usageLimit ?? "∞"}`;
      case "perUser":
        return row.perUserUsageLimit ?? "∞";
      case "validFrom":
        return formatValidFrom(row);
      case "validTo":
        return formatValidTo(row);
      case "status":
        return <StatusChip value={row.status} />;
      default:
        return "—";
    }
  };

  return (
    <Box sx={{ textAlign: "left" }}>
      <ToastContainer />

      <Card sx={{ boxShadow: 1, mb: 2.5 }}>
        <CardContent>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              gap: 2,
              flexWrap: "wrap",
            }}
          >
            <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap", flex: "1 1 600px", alignItems: "center" }}>
              <TextField
                size="small"
                placeholder="Search coupon code"
                value={search}
                onChange={handleSearchChange}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon fontSize="small" sx={{ color: "#999" }} />
                    </InputAdornment>
                  ),
                }}
                sx={{ minWidth: 220 }}
              />
              <FormControl size="small" sx={{ minWidth: 160 }}>
                <InputLabel>Applies to</InputLabel>
                <Select label="Applies to" value={typeFilter} onChange={handleTypeChange}>
                  <MenuItem value="">All</MenuItem>
                  {TARGET_TYPES.map((t) => (
                    <MenuItem key={t.value} value={t.value}>
                      {t.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              {typeFilter && (
                <Autocomplete
                  size="small"
                  sx={{ minWidth: 240 }}
                  options={targetOptions}
                  value={targetFilter}
                  onChange={handleTargetChange}
                  getOptionLabel={(option) => option.label || ""}
                  isOptionEqualToValue={(a, b) => a.id === b.id}
                  noOptionsText="Nothing to choose from"
                  renderInput={(params) => (
                    <TextField {...params} label={`Target ${targetTypeLabel(typeFilter)}`} />
                  )}
                />
              )}
              <FormControl size="small" sx={{ minWidth: 170 }}>
                <InputLabel>Discount Type</InputLabel>
                <Select label="Discount Type" value={discountTypeFilter} onChange={handleDiscountTypeChange}>
                  <MenuItem value="">All types</MenuItem>
                  {DISCOUNT_TYPES.map((t) => (
                    <MenuItem key={t.value} value={t.value}>
                      {t.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl size="small" sx={{ minWidth: 150 }}>
                <InputLabel>Status</InputLabel>
                <Select label="Status" value={statusFilter} onChange={handleStatusChange}>
                  <MenuItem value="">All</MenuItem>
                  <MenuItem value="ACTIVE">Active</MenuItem>
                  <MenuItem value="INACTIVE">Inactive</MenuItem>
                </Select>
              </FormControl>
            </Box>

            <Box sx={{ display: "flex", gap: 1.5, alignItems: "center" }}>
              <ColumnsMenuButton allColumns={columns} visibleColumns={visibleColumns} onToggle={toggleColumn} />
              <Button
                variant="contained"
                color="primary"
                startIcon={<AddIcon />}
                onClick={openAdd}
                {...BUTTON_STYLES.primary}
                sx={{ minWidth: { xs: "120px", md: "auto" }, fontSize: { xs: "10px", md: "12px" } }}
              >
                Add Coupon
              </Button>
            </Box>
          </Box>
        </CardContent>
      </Card>

      <Paper sx={{ width: "100%", overflow: "hidden", boxShadow: 2, borderRadius: 3 }}>
        <TableContainer sx={{ maxHeight: "calc(100vh - 300px)", minHeight: 420 }}>
          <Table stickyHeader size="small" sx={{ width: "100%", minWidth: 1200 }}>
            <TableHead>
              <TableRow>
                {visibleColumnDefs.map((c) => (
                  <TableCell
                    key={c.key}
                    align={c.align}
                    sx={{
                      ...c.sx,
                      padding: "10px 12px",
                      backgroundColor: ACCENT,
                      color: "white",
                      fontWeight: 600,
                    }}
                  >
                    {c.label}
                  </TableCell>
                ))}
                <TableCell
                  align="center"
                  sx={{
                    minWidth: 90,
                    padding: "10px 12px",
                    backgroundColor: ACCENT,
                    color: "white",
                    fontWeight: 600,
                    position: "sticky",
                    right: 0,
                    zIndex: 4,
                  }}
                >
                  Actions
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <AssessmentTableSkeleton columns={visibleColumnDefs.length + 1} />
              ) : coupons.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={visibleColumnDefs.length + 1} sx={{ textAlign: "center", py: 4 }}>
                    <AssessmentEmptyState message="No coupons found" hint="Try changing your search or filters." />
                  </TableCell>
                </TableRow>
              ) : (
                coupons.map((row) => (
                  <TableRow
                    key={row.id}
                    hover
                    sx={{ "&:nth-of-type(odd)": { backgroundColor: "rgba(0,0,0,0.02)" } }}
                  >
                    {visibleColumnDefs.map((c) => (
                      <TableCell key={c.key} align={c.align} sx={{ padding: "10px 12px", ...c.sx }}>
                        {renderCell(c.key, row)}
                      </TableCell>
                    ))}
                    <TableCell
                      align="center"
                      sx={{ padding: "10px 12px", position: "sticky", right: 0, backgroundColor: "#fff", zIndex: 1 }}
                    >
                      <Stack direction="row" spacing={0.5} justifyContent="flex-start">
                        <Tooltip title="Edit Coupon" arrow placement="top">
                          <IconButton
                            size="small"
                            color="primary"
                            onClick={() => {
                              setEditing(row);
                              setFormOpen(true);
                            }}
                            sx={{ "&:hover": { backgroundColor: "primary.light", color: "white" } }}
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title={row.active ? "Deactivate Coupon" : "Already inactive"} arrow placement="top">
                          <span>
                            <IconButton
                              size="small"
                              color="error"
                              disabled={!row.active}
                              onClick={() => setDeactivateTarget(row)}
                              sx={{ "&:hover": { backgroundColor: "error.light", color: "white" } }}
                            >
                              <BlockIcon fontSize="small" />
                            </IconButton>
                          </span>
                        </Tooltip>
                      </Stack>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          rowsPerPageOptions={PAGE_SIZE_OPTIONS}
          component="div"
          count={totalElements}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          sx={{
            borderTop: "1px solid",
            borderColor: "divider",
            "& .MuiTablePagination-toolbar": { paddingLeft: 2, paddingRight: 2 },
          }}
        />
      </Paper>

      <AssessmentCouponForm
        open={formOpen}
        coupon={editing}
        courses={courses}
        series={series}
        onClose={() => setFormOpen(false)}
        onSave={handleSave}
      />

      <DeleteConfirmationDialog
        open={Boolean(deactivateTarget)}
        onClose={() => setDeactivateTarget(null)}
        onConfirm={handleDeactivate}
        title="Confirm Deactivate"
        message={`Deactivating "${deactivateTarget?.code ?? "this coupon"}" makes checkout reject the code. The coupon stays in this list as Inactive and can be re-activated by editing it.`}
        confirmText="DEACTIVATE"
        confirmButtonText="Deactivate"
        cancelButtonText="Cancel"
      />
    </Box>
  );
}

export default AssessmentCoupons;
